/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import styles from './MegaHero.module.scss';
import Carousel from './Carousel';
import HeroHeaderContent from './HeroHeaderContent';
import type { ContentStackMegaHero } from '@/features/contentstack/interface';
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useCitrusHomepageData } from '@/features/citrus/api/getCitrusHomepageData';
import { isCitrusBannerWithLink } from '@/features/citrus/utils/isCitrusBannerWithLink';
import { mapCitrusBannerToContentStackBanner } from '@/features/citrus/utils/mapCitrusBannerToContentStackBanner';
import { reportCitrusImpression } from '@/features/citrus/utils/reportCitrusImpression';
import { useLabels } from '@/hooks/useLabels';
import { usePathname } from 'next/navigation';
import { routePaths } from '@/constants/routePaths';
import { resolveColor } from './utils';

export type MegaHeroProps = {
  options: ContentStackMegaHero;
};

type HeroSlide = ContentStackMegaHero['hero'][number];

const labelMap = {
  clickForDetails: 'label_click_for_details',
} as const;

const MegaHero = ({ options }: MegaHeroProps) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const pathname = usePathname();
  const isHomePage = pathname === routePaths.blankURL;
  const { data: citrusData } = useCitrusHomepageData({ enabled: isHomePage });
  const labels = useLabels(labelMap);

  // Authored heroes without images are used as text-only fallback
  const authoredHeroes = useMemo(() => options.hero.filter(({ azmn }) => !azmn), [options.hero]);

  // Keep authored slides with images plus AZMN placeholder slots
  const cmsSlides = useMemo(
    () => options.hero.filter((hero) => (hero.azmn ? true : !!hero.image?.image_url)),
    [options.hero],
  );

  // Replace AZMN placeholder slots with Citrus banners by matching position
  const [slides, citrusAdIds] = useMemo(() => {
    const citrusBanners =
      citrusData?.ads?.find((item) => item.slotId === 'carousel-banner')?.banners ?? [];

    const citrusByPosition = new Map<number, { slide: HeroSlide; adId: string }>();
    citrusBanners.forEach((banner) => {
      if (!isCitrusBannerWithLink(banner)) {
        return;
      }
      const mapped = mapCitrusBannerToContentStackBanner(banner, labels.clickForDetails);
      citrusByPosition.set(banner.position, {
        slide: { azmn: false, image: mapped.image, link: mapped.link, legal: mapped.legal },
        adId: banner.citrusAdId,
      });
    });

    // placeholder_value is 1-indexed (e.g. "2" → Citrus position 1)
    const merged = cmsSlides
      .map((hero) => {
        if (!hero.azmn) {
          return { slide: hero, adId: '' };
        }
        const position = parseInt(hero.placeholder_value ?? '', 10) - 1;
        const citrus = !isNaN(position) ? citrusByPosition.get(position) : undefined;
        return citrus ? { slide: citrus.slide, adId: citrus.adId } : null;
      })
      .filter(Boolean) as Array<{ slide: HeroSlide; adId: string }>;

    return [merged.map((m) => m.slide), merged.map((m) => m.adId)];
  }, [citrusData?.ads, cmsSlides, labels.clickForDetails]);

  // Identifies which slide occupies each position so Carousel can be remounted
  // (and currentSlide reset) only when that identity actually changes
  const slidesIdentityKey = useMemo(
    () =>
      slides.map((slide, index) => citrusAdIds[index] || slide.image?.image_url || index).join('|'),
    [slides, citrusAdIds],
  );

  // Keeps MegaHero's index and Carousel's internal index from drifting apart
  // when Citrus data resolves and reshuffles the merged slide list
  useEffect(() => {
    setCurrentSlide(0);
  }, [slidesIdentityKey]);

  const hero = slides[currentSlide] ?? authoredHeroes[0];

  if (!hero) {
    return null;
  }

  const mobileBackgroundColor = resolveColor(hero.mobile_background_color);
  const heroStyles = mobileBackgroundColor
    ? ({ '--mega-hero-mobile-background-color': mobileBackgroundColor } as CSSProperties)
    : undefined;

  const handleSlideChange = (index: number) => {
    setCurrentSlide(index);
    const adId = citrusAdIds[index];
    if (adId) {
      void reportCitrusImpression(adId);
    }
  };

  return (
    <div className={styles.MegaHero} style={heroStyles}>
      <Carousel
        key={slidesIdentityKey}
        slides={slides}
        autoRotationSpeed={options.auto_rotation_speed}
        controlsTheme={hero.controls_theme}
        onSlideChange={handleSlideChange}
      />
      {!slides.length && (
        <HeroHeaderContent slides={authoredHeroes} currentSlide={0} previousSlide={0} />
      )}
    </div>
  );
};

export default MegaHero;
